import React, { useEffect, useState } from "react";
import "./InstallmentsStudentDetailsEdit.scss";
import CustomTextInput from "../CustomTextInput/CustomTextInput";
import CustomNumberInput from "../CustomNumberInput/CustomNumberInput";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Button from "../Button/Button";
import axios from "axios";
import DropDown from "../DropDown/DropDown";
import {
  FaMoneyBillWave,
  FaMedal,
  FaCalendarAlt,
  FaPercentage,
  FaGripVertical,
  FaPlusCircle,
  FaTrashAlt,
  FaUndo,
  FaSave,
  FaExclamationTriangle,
  FaBuildingColumns,
  FaCoins,
  FaRegClock,
} from "react-icons/fa";
import { MdDiscount, MdOutlineDragIndicator } from "react-icons/md";

const BE_URL = process.env.REACT_APP_API_BASE_URL;

const InstallmentsStudentDetailsEdit = ({
  session,
  student_id,
  student_roll_no,
  title,
  fee,
  finalFee,
  scholarshipAmount = 0,
  additial_amount = 0,
  installments,
  setInstallments,
  handleRefresh,
}) => {
  const [accounts, setaccounts] = useState([]);

  const fetchcategories = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
      const response = await axios.get(
        `${BE_URL}/api/general/categories/Accounts`,
        {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        }
      );
      setaccounts(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      if (error.response && error.response.status === 401) {
        alert("Session expired. Please login again.");
      } else {
        alert("Failed to fetch categories. Please try again later.");
      }
    }
  };

  const handleSaveInstallment = async (installment, isUpdate = false) => {
    if (!installment.account) {
      alert("Please select the account!");
      return;
    }
    const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
    const url = `${BE_URL}/api/installment${
      isUpdate ? `/${installment.id}` : ""
    }`;
    const method = isUpdate ? "put" : "post";
    try {
      const response = await axios[method](
        url,
        { student_id, student_roll_no, installment },
        {
          headers: { Authorization: `Bearer ${tokens.accessToken}` },
        }
      );

      if (!isUpdate) {
        const newInstallments = [...installments];
        newInstallments[installments.length - 1] = {
          ...newInstallments[installments.length - 1],
          id: response.data.id,
        };
        setInstallments(newInstallments);
      }

      handleRefresh();

      alert(`Installment ${isUpdate ? "updated" : "added"} successfully.`);
    } catch (error) {
      console.error("Error saving installment:", error);
      alert("Failed to save installment.");
    }
  };

  const handleDeleteInstallment = async (id, indexToRemove) => {
    if (!window.confirm("Are you sure you want to delete the installment?")) {
      return;
    }
    const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
    try {
      await axios.delete(`${BE_URL}/api/installment/${id}`, {
        headers: { Authorization: `Bearer ${tokens.accessToken}` },
      });

      const filteredInstallments = installments.filter(
        (_, index) => index !== indexToRemove
      );
      setInstallments(filteredInstallments);

      handleRefresh();

      alert("Installment deleted successfully.");
    } catch (error) {
      console.error("Error deleting installment:", error);
      alert("Failed to delete installment.");
    }
  };

  const handleRefundInstallment = async (id) => {
    if (!window.confirm("Are you sure you want to refund this installment?")) {
      return;
    }

    const refundAmount = prompt(
      "Please enter the refund amount (enter 0 if not applicable):"
    );
    if (refundAmount === null) {
      return;
    }

    const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
    try {
      await axios.put(
        `${BE_URL}/api/installment/refund/${id}`,
        { refundAmount },
        {
          headers: { Authorization: `Bearer ${tokens.accessToken}` },
        }
      );

      handleRefresh();

      alert(
        "Installment refunded successfully. Please check Expenses for Refund Details."
      );
    } catch (error) {
      console.error("Error refunding installment:", error);
      alert("Failed to refund installment.");
    }
  };

  useEffect(() => {
    fetchcategories();
  }, []);

  const handleAccountChange = (index, value) => {
    const acc = accounts.filter((item) => item.id === value);
    const newInstallments = (installments || []).map((inst, idx) => {
      if (idx === index) {
        return { ...inst, account: value };
      }
      return inst;
    });
    setInstallments(newInstallments);
  };

  const addDiscountRow = (index) => {
    const newInstallments = [...installments];
    if (!newInstallments[index].discount) {
      newInstallments[index].discount = { name: "", amount: null };
    }
    setInstallments(newInstallments);
  };

  const removeDiscountRow = (index) => {
    const newInstallments = [...installments];
    delete newInstallments[index].discount;
    setInstallments(newInstallments);
  };

  const handleInstallmentChange = (index, field, value) => {
    const newInstallments = installments.map((inst, idx) =>
      idx === index ? { ...inst, [field]: value } : inst
    );
    setInstallments(newInstallments);
  };

  const handleDiscountChange = (index, field, value) => {
    const newInstallments = [...installments];
    newInstallments[index].discount[field] = value;
    setInstallments(newInstallments);
  };

  const addNewInstallment = () => {
    const newInstallment = {
      installment_name: "",
      amount: "",
      due_date: new Date().toISOString().split("T")[0],
      account: null,
      discount: { name: null, amount: null },
      status: "UnPaid",
      installment_number: installments.length + 1,
    };
    setInstallments([...installments, newInstallment]);
  };

  const saveInstallment = (installment, index) => {
    installment = { ...installment, session };
    if (installment.id) {
      handleSaveInstallment(installment, true);
    } else {
      handleSaveInstallment(installment);
    }
  };

  const totalInstallments = installments.reduce((acc, curr) => {
    const discountAmount = curr.discount ? Number(curr.discount.amount) : 0;
    return acc + (Number(curr.amount) - discountAmount);
  }, 0);

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const updatedInstallments = Array.from(installments);
    const [movedItem] = updatedInstallments.splice(result.source.index, 1);
    updatedInstallments.splice(result.destination.index, 0, movedItem);

    updatedInstallments.forEach((inst, idx) => {
      inst.installment_number = idx + 1;
    });

    setInstallments(updatedInstallments);

    try {
      await updateInstallmentNumbersAPI(updatedInstallments);
    } catch (error) {
      console.error("Failed to update installment numbers, reverting changes.");
      setInstallments(installments);
    }
  };

  const updateInstallmentNumbersAPI = async (updatedInstallments) => {
    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));

      const payload = updatedInstallments.map((inst) => ({
        id: inst.id,
        installment_number: inst.installment_number,
      }));

      const response = await axios.post(
        `${BE_URL}/api/updateInstallmentNumbers/`,
        { installments: payload },
        {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating installment numbers:", error);
      throw error;
    }
  };

  return (
    <div className="installments-edit">
      <div className="fee-summary">
        <div className="fee-summary-header">
          <h2>{title}</h2>
        </div>
        <div className="fee-summary-cards">
          <div className="summary-card total-fee">
            <div className="card-icon">
              <FaMoneyBillWave />
            </div>
            <div className="card-content">
              <h3>Total Fee</h3>
              <span className="amount">₹ {fee}</span>
            </div>
          </div>

          <div className="summary-card scholarshipcard">
            <div className="card-icon">
              <FaMedal />
            </div>
            <div className="card-content">
              <h3>Scholarship Amount</h3>
              <span className="amount">₹ {scholarshipAmount || 0}</span>
            </div>
          </div>

          <div className="summary-card discount">
            <div className="card-icon">
              <MdDiscount />
            </div>
            <div className="card-content">
              <h3>Additional Discount</h3>
              <span className="amount">₹ {additial_amount || 0}</span>
            </div>
          </div>

          <div className="summary-card installments">
            <div className="card-icon">
              <FaCoins />
            </div>
            <div className="card-content">
              <h3>Total Installments</h3>
              <span className="amount">₹ {totalInstallments}</span>
            </div>
          </div>
        </div>

        {finalFee !== totalInstallments && (
          <div className="fee-mismatch-alert">
            <FaExclamationTriangle />
            <span>
              The total of installments (₹{totalInstallments}) does not match
              the final fee (₹{finalFee}).
            </span>
          </div>
        )}
      </div>

      <div className="installments-table">
        <div className="installments-header">
          <div className="header-cell drag-handle"></div>
          <div className="header-cell name">Name</div>
          <div className="header-cell amount">Amount</div>
          <div className="header-cell late-fee">Late Fee</div>
          <div className="header-cell discount">Discount</div>
          <div className="header-cell account">Account</div>
          <div className="header-cell due-date">Due Date</div>
          <div className="header-cell actions">Actions</div>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="installments-list">
            {(provided) => (
              <div
                className="installments-rows"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {installments.map((inst, index) => (
                  <Draggable
                    key={inst.id || `new-${index}`}
                    draggableId={
                      inst.id ? `item-${inst.id}` : `new-item-${index}`
                    }
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`installment-row ${
                          snapshot.isDragging ? "dragging" : ""
                        } ${inst.status === "Paid" ? "paid" : ""}`}
                      >
                        <div
                          className="cell drag-handle"
                          {...provided.dragHandleProps}
                        >
                          <MdOutlineDragIndicator />
                        </div>

                        <div className="cell name">
                          <CustomTextInput
                            placeholder="Installment Name"
                            value={inst.installment_name}
                            onChange={(e) =>
                              handleInstallmentChange(
                                index,
                                "installment_name",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="cell amount">
                          <div className="input-with-icon">
                            <FaMoneyBillWave className="input-icon" />
                            <CustomNumberInput
                              placeholder="Amount"
                              value={inst.amount}
                              onChange={(e) =>
                                handleInstallmentChange(
                                  index,
                                  "amount",
                                  Number(e)
                                )
                              }
                            />
                          </div>
                        </div>

                        <div className="cell late-fee">
                          <div className="input-with-icon">
                            <FaRegClock className="input-icon" />
                            <CustomNumberInput
                              placeholder="Late Fee"
                              value={inst.late_fee}
                              onChange={(e) =>
                                handleInstallmentChange(
                                  index,
                                  "late_fee",
                                  Number(e)
                                )
                              }
                            />
                          </div>
                        </div>

                        <div className="cell discount">
                          {inst.discount ? (
                            <div className="discount-fields">
                              <div className="discount-field">
                                <CustomTextInput
                                  placeholder="Discount Name"
                                  value={inst.discount.name}
                                  onChange={(e) =>
                                    handleDiscountChange(
                                      index,
                                      "name",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="discount-field">
                                <div className="input-with-icon">
                                  <FaPercentage className="input-icon" />
                                  <CustomNumberInput
                                    value={inst.discount.amount}
                                    placeholder="Amount"
                                    onChange={(e) =>
                                      handleDiscountChange(
                                        index,
                                        "amount",
                                        Number(e)
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <button
                                className="remove-discount-btn"
                                onClick={() => removeDiscountRow(index)}
                              >
                                <FaTrashAlt />
                              </button>
                            </div>
                          ) : (
                            <button
                              className="add-discount-btn"
                              onClick={() => addDiscountRow(index)}
                            >
                              <MdDiscount /> Add Discount
                            </button>
                          )}
                        </div>

                        <div className="cell account">
                          <div className="account-select">
                            {/* <FaCalendarAlt className="select-icon" /> */}
                            <DropDown
                              options={accounts.map((item) => item.name)}
                              value={inst.account || ""}
                              onChange={(e) => handleAccountChange(index, e)}
                            />
                          </div>
                        </div>

                        <div className="cell due-date">
                          <div className="input-with-icon">
                            {/* <FaCalendarAlt className="input-icon" /> */}
                            <CustomTextInput
                              type="date"
                              placeholder="Due Date"
                              value={
                                inst.due_date
                                  ? inst.due_date.split("T")[0]
                                  : null
                              }
                              onChange={(e) =>
                                handleInstallmentChange(
                                  index,
                                  "due_date",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>

                        <div className="cell actions">
                          {inst.status === "Paid" ? (
                            <button
                              className="refund-button"
                              onClick={() => handleRefundInstallment(inst.id)}
                            >
                              <FaUndo /> Refund
                            </button>
                          ) : (
                            <div className="action-buttons">
                              <button
                                className="save-button"
                                onClick={() => saveInstallment(inst, index)}
                              >
                                <FaSave /> Save
                              </button>
                              <button
                                className="delete-button"
                                onClick={() =>
                                  handleDeleteInstallment(inst.id, index)
                                }
                              >
                                <FaTrashAlt />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <div className="add-installment-container">
        <button className="add-installment-button" onClick={addNewInstallment}>
          <FaPlusCircle /> Add Installment
        </button>
      </div>
    </div>
  );
};

export default InstallmentsStudentDetailsEdit;
